import type { CSSProperties } from 'react';
import type { ThemeSummary } from '@icetus/theme-kit';

/**
 * 相对亮度（WCAG 口径）。只用来判断「谁更暗」，不参与对比度断言。
 *
 * `palette` 里的值都是令牌解析后的十六进制，但客户改品牌色之后理论上可能
 * 进来 `rgb(...)` 之类的东西 —— 解析不了就返回 0.5，让两边「差不多」，
 * 由下面的比较保持旧行为，不让缩略图因为一个色值格式整块变透明。
 */
export function luminance(color: string): number {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!match) return 0.5;
  let hex = match[1]!;
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/**
 * `showcase` 的缩略块要画成「一张满铺的图」：块本身深、文字浅压在上面。
 *
 * 这里**按亮度挑**，而不是按语义位挑 `text` / `bg`：
 * 浅色主题（窑变）里 `text` 是深色，拿它当图底正好；
 * 但夜曲是全站深色，`text` 反而是浅的 —— 按语义位取色会让付费主题卡
 * 变成「白底黑条」，与它「深色电光」的性格完全相反（截图里就是这样）。
 */
export function heroTone(palette: ThemeSummary['palette']): { hero: string; ink: string } {
  return luminance(palette.text) <= luminance(palette.bg)
    ? { hero: palette.text, ink: palette.bg }
    : { hero: palette.bg, ink: palette.text };
}

/**
 * 主题缩略示意。
 *
 * 画的是"骨架 + 配色"，不是截图：客户看到的必须是自己品牌色进来之后的样子。
 * 形状跟着 `archetype` 走 —— 三套主题的差别在"图和字怎么摆"，
 * 统一画成"页头 + 两行字 + 按钮"的话，卡片上永远只有颜色在变，
 * 客户就会以为换主题只是换色（线上确实被这么投诉过）。
 *
 * 抽成组件是因为它现在有三个使用方：站点设置里的选择卡、主题市场的商品卡、
 * 以及将来的主题详情。「差不多就行」的第二份实现一定会漏掉某个
 * `--thumb-*` 变量，然后那张卡片在深色主题下变成一块白底。
 */
export function ThemeThumb({ theme }: { theme: ThemeSummary }) {
  const tone = heroTone(theme.palette);

  return (
    <span
      className="theme-card__swatch"
      data-archetype={theme.archetype}
      style={
        {
          /* 底色也走变量：showcase 的"图铺满整块"要用深色画布，
             写成内联 background 就再也覆盖不掉了（内联优先于任何选择器） */
          '--thumb-canvas': theme.palette.bg,
          '--thumb-header': theme.palette.headerBg,
          '--thumb-header-ink': theme.palette.headerText,
          '--thumb-accent': theme.palette.accent,
          '--thumb-ink': theme.palette.text,
          '--thumb-line': theme.palette.border,
          '--thumb-media': theme.palette.surface,
          '--thumb-brand': theme.palette.brand,
          /* showcase 专用：满铺「图」的底色与压在上面的字色。
             这两个值要成对出现，所以由上面的 heroTone 一起算出来 */
          '--thumb-hero': tone.hero,
          '--thumb-hero-ink': tone.ink,
        } as CSSProperties
      }
    >
      <span className="theme-card__bar">
        <span className="theme-card__dot" />
      </span>
      <span className="theme-card__body">
        <span className="theme-card__copy">
          <span className="theme-card__line" />
          <span className="theme-card__line theme-card__line--short" />
          <span className="theme-card__btn" />
        </span>
        <span className="theme-card__media" />
      </span>
    </span>
  );
}
